/**
 * Local at-rest helpers — SPEC §8.3 / §16.
 * Vault key stays in memory after unlock; never persist vault_key_b64.
 */

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToB64(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

function asBufferSource(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

async function importAesKey(vaultKeyB64: string): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", asBufferSource(b64ToBytes(vaultKeyB64)), "AES-GCM", false, [
    "encrypt",
    "decrypt",
  ]);
}

export async function encryptUtf8(vaultKeyB64: string, plaintext: string): Promise<string> {
  const key = await importAesKey(vaultKeyB64);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    new TextEncoder().encode(plaintext),
  );
  const packed = new Uint8Array(iv.length + ct.byteLength);
  packed.set(iv, 0);
  packed.set(new Uint8Array(ct), iv.length);
  return bytesToB64(packed);
}

export async function decryptUtf8(vaultKeyB64: string, packedB64: string): Promise<string> {
  const key = await importAesKey(vaultKeyB64);
  const packed = b64ToBytes(packedB64);
  const iv = packed.slice(0, 12);
  const ct = packed.slice(12);
  const pt = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: asBufferSource(iv) },
    key,
    asBufferSource(ct),
  );
  return new TextDecoder().decode(pt);
}

/** Strip ephemeral vault_key_b64 before durable storage. */
export function stripVaultKeyFromWrapped(wrappedJson: string): {
  durableWrappedJson: string;
  vaultKeyB64: string;
} {
  const obj = JSON.parse(wrappedJson) as Record<string, unknown>;
  const vaultKeyB64 = String(obj.vault_key_b64 ?? "");
  delete obj.vault_key_b64;
  return { durableWrappedJson: JSON.stringify(obj), vaultKeyB64 };
}
